<template>
  <v-card class="mb-8">
    <v-card-title>レシートプリンタ</v-card-title>
    <v-card-text>
      <v-text-field
        ref="printIpAddressField"
        :value="form.printIpAddress ? form.printIpAddress.split(':')[0] : ''"
        :error="!form.printIpAddress"
        label="プリンタ IPアドレス"
        placeholder="IPアドレス"
        minlength="7"
        maxlength="15"
        pattern="^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$"
        :rules="[
          value => /^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/.test(value) || '無効な値です',
        ]"
        required
        @input="changePrintIpAddress($event)"
      />

      <div class="d-flex align-center flex-wrap mb-4">
        <v-btn
          x-small
          outlined
          color="grey darken-1"
          class="mr-1 mb-1"
          :href="printerWebConfigUrl"
          :disabled="!printerWebConfigUrl"
          target="_blank"
        >
          WebConfig
        </v-btn>
        <v-btn
          x-small
          outlined
          color="warning"
          class="mr-1 mb-1"
          :href="printerCertUrl"
          :disabled="!printerCertUrl"
          target="_blank"
        >
          証明書を受け入れる
        </v-btn>
        <v-btn
          x-small
          outlined
          color="primary"
          class="mr-1 mb-1"
          :loading="checkConnectionStatus.inProgress"
          @click="testConnection"
        >
          接続テスト
        </v-btn>
        <v-slide-x-transition>
          <span
            v-show="checkConnectionStatus.on && !checkConnectionStatus.inProgress"
            class="mr-1"
          >
            <v-icon
              v-if="isConnected"
              small
              color="success"
            >
              {{ icons.mdiCheckCircleOutline }}
            </v-icon>
            <v-icon
              v-else
              small
              color="error"
            >
              {{ icons.mdiCloseCircleOutline }}
            </v-icon>
          </span>
        </v-slide-x-transition>
        <v-btn
          x-small
          outlined
          color="success"
          class="mr-1 mb-1"
          @click="openPrinterDiagnostic"
        >
          診断
        </v-btn>
      </div>
      <printer-diagnostic-dialog />

      <v-divider class="my-4"></v-divider>

      <!-- サーバー経由印刷（SDP）設定。polling_id/password はこのレシートプリンタ単位 -->
      <v-switch
        v-model="sdpForm.sdpEnabled"
        label="サーバー経由で印刷する（サーバーダイレクトプリント）"
        hint="ONにすると会計伝票・領収書などをプリンタがサーバーから取得して印刷します。下の認証情報をプリンタのWebConfig（サーバー認証）に設定してください"
        persistent-hint
        class="mt-0"
      />
      <v-text-field
        v-model="sdpForm.sdpDeviceId"
        label="プリンタ識別子（devid）"
        hint="レシートプリンタがHub機本体の場合は local_printer。Hub配下に登録した場合はWebConfig上の登録名"
        persistent-hint
        :disabled="!sdpForm.sdpEnabled"
        class="mt-4"
      />

      <!--
        会計とキッチンで同じ devid を指すと、両方の伝票が同じ物理プリンタに出る。
        1台運用なら正しいが、別々のプリンタを意図した誤設定の可能性が高いため警告する（保存は許可）。
      -->
      <v-alert
        v-if="conflictingKitchenPrinterNames.length > 0"
        type="warning"
        dense
        text
        class="mt-3 mb-0"
      >
        この識別子（{{ sdpForm.sdpDeviceId }}）はキッチンプリンタ「{{ conflictingKitchenPrinterNames.join('、') }}」と同じです。
        会計伝票とキッチン伝票が<strong>同じプリンタに印刷</strong>されます。別のプリンタに出したい場合は devid を変えてください
        （1台で兼用する場合はこのままで問題ありません）。
      </v-alert>

      <!--
        物理1台でレシート/キッチン兼用する場合の認証情報共用。
        1台のプリンタは1つの polling_id でしかポーリングできないため、共用するとキッチン伝票も
        レシート伝票も同じ1回のポーリングで届く（両伝票が同じプリンタに印字される）。
      -->
      <template v-if="sdpForm.sdpEnabled && kitchenPrinterOptions.length > 0">
        <v-switch
          v-model="shareWithKitchen"
          label="キッチンプリンタと認証情報を共用する（1台で兼用）"
          hint="同じ物理プリンタでレシートもキッチンも印刷する場合にON。キッチンと同じポーリングID/パスワードを使います"
          persistent-hint
          dense
          class="mt-4"
        />
        <div
          v-if="shareWithKitchen"
          class="d-flex align-center mt-2"
        >
          <v-select
            v-model="shareSourceId"
            :items="kitchenPrinterOptions"
            label="共用するキッチンプリンタ"
            dense
            outlined
            hide-details
            style="max-width: 280px;"
          />
          <v-btn
            small
            color="primary"
            class="ml-2"
            :ripple="false"
            :loading="isSharing"
            :disabled="!shareSourceId"
            @click="applyShareCredentials"
          >
            共用する
          </v-btn>
        </div>
        <p
          v-if="shareWithKitchen"
          class="text-caption text--secondary mt-1 mb-0"
        >
          共用すると下の認証情報がキッチンと同じ値になります。devid も同じ（例: local_printer）にしてください。
        </p>
      </template>

      <!-- SDP認証情報（このレシートプリンタ単位）。WebConfigのサーバー認証に設定する -->
      <div
        v-if="sdpForm.sdpEnabled"
        class="mt-4 pa-3"
        style="background: rgba(0,0,0,0.03); border-radius: 4px;"
      >
        <p class="text-body-2 font-weight-medium mb-2">
          SDP認証情報（このレシートプリンタ用）
        </p>
        <div class="mb-1 text-body-2">
          サーバーURL:
          <strong style="font-family: monospace;">{{ receiptPrinter.sdpServerUrl }}</strong>
          <v-icon
            small
            class="ml-1"
            style="cursor: pointer;"
            @click="copyToClipboard(receiptPrinter.sdpServerUrl, 'サーバーURL')"
          >
            {{ icons.mdiContentCopy }}
          </v-icon>
        </div>
        <div class="mb-1 text-body-2">
          ポーリングID:
          <strong style="font-family: monospace;">{{ receiptPrinter.sdpPollingId || '未発行' }}</strong>
          <v-icon
            v-if="receiptPrinter.sdpPollingId"
            small
            class="ml-1"
            style="cursor: pointer;"
            @click="copyToClipboard(receiptPrinter.sdpPollingId, 'ポーリングID')"
          >
            {{ icons.mdiContentCopy }}
          </v-icon>
        </div>
        <div class="mb-2 text-body-2">
          パスワード:
          <template v-if="issuedPassword">
            <strong style="font-family: monospace;">{{ isSdpPasswordVisible ? issuedPassword : '••••••••' }}</strong>
            <v-icon
              small
              class="ml-1"
              style="cursor: pointer;"
              @click="isSdpPasswordVisible = !isSdpPasswordVisible"
            >
              {{ isSdpPasswordVisible ? icons.mdiEyeOff : icons.mdiEye }}
            </v-icon>
            <v-icon
              small
              class="ml-1"
              style="cursor: pointer;"
              @click="copyToClipboard(issuedPassword, 'パスワード')"
            >
              {{ icons.mdiContentCopy }}
            </v-icon>
          </template>
          <span
            v-else-if="receiptPrinter.sdpPollingId"
            class="text--secondary"
          >設定済み（再発行で確認できます）</span>
          <strong v-else>未発行</strong>
        </div>
        <v-btn
          small
          outlined
          color="primary"
          :ripple="false"
          :loading="isIssuing"
          :disabled="shareWithKitchen"
          @click="onClickIssueSdpCredentials"
        >
          ID・パスワードを{{ receiptPrinter.sdpPollingId ? '再発行' : '発行' }}
        </v-btn>
        <p
          v-if="shareWithKitchen"
          class="text-caption text--secondary mt-1 mb-0"
        >
          キッチンと共用中のため、ここでの独立発行は無効です（共用を解除すると発行できます）。
        </p>
        <p
          v-else-if="receiptPrinter.sdpPollingId"
          class="text-caption text--secondary mt-1 mb-0"
        >
          再発行すると既存のID・パスワードは無効になり、プリンタのWebConfig側も再設定が必要です。
        </p>

        <!-- 再発行の確認ダイアログ（誤タップによる意図しないリセット防止） -->
        <v-dialog
          v-model="isReissueConfirmOpen"
          max-width="420"
          persistent
        >
          <v-card>
            <v-card-title>ID・パスワードを再発行しますか？</v-card-title>
            <v-card-text>
              再発行すると現在のポーリングID・パスワードは<strong>すぐに無効</strong>になり、このプリンタからの印刷が止まります。<br>
              再発行後は、プリンタのWebConfig（サーバー認証）に新しいID・パスワードを設定し直してください。
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                text
                :ripple="false"
                :disabled="isIssuing"
                @click="isReissueConfirmOpen = false"
              >
                キャンセル
              </v-btn>
              <v-btn
                color="error"
                :ripple="false"
                :loading="isIssuing"
                @click="confirmReissueSdpCredentials"
              >
                再発行する
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>

      <v-divider class="my-4"></v-divider>

      <v-file-input
        ref="receiptPreviewBackgroundImageField"
        v-model="form.receiptPreviewBackgroundImage"
        accept="image/*"
        label="レシートプレビュー背景"
        :rules="[lessThanOrEqualTo2Gb]"
        show-size
      >
        <template #append-outer>
          <v-btn
            color="secondary"
            :ripple="false"
            :disabled="!(currentClub.receiptPreviewBackgroundImage || form.receiptPreviewBackgroundImage)"
            @click="previewBackgroundImage('receiptPreviewBackgroundImage')"
          >
            プレビュー
          </v-btn>
        </template>
      </v-file-input>

      <div class="d-flex justify-end mt-4">
        <v-btn
          color="primary"
          :ripple="false"
          :loading="isSubmitting"
          :disabled="hasError"
          @click="update"
        >
          保存
        </v-btn>
      </div>
    </v-card-text>

    <!-- プレビューダイアログ -->
    <v-dialog
      :value="!!onPreviewKey && !!backgroundImagePreviewUrl"
      max-width="440"
      @input="value => { if (!value) { onPreviewKey = null; backgroundImagePreviewUrl = null } }"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          レシートプレビュー背景
          <v-btn
            icon
            @click="onPreviewKey = null; backgroundImagePreviewUrl = null"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="d-flex justify-center">
          <div
            class="preview-bg"
            :style="{ backgroundImage: `url(${backgroundImagePreviewUrl})` }"
          ></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar
      :value="updated"
      bottom
      right
      vertical
      :timeout="-1"
    >
      <p class="mb-0">
        プリンタ設定の変更を反映するため、リロードしてください
      </p>
      <template #action="{ attrs }">
        <v-btn
          :ripple="false"
          color="primary"
          v-bind="attrs"
          @click="$router.go(0)"
        >
          リロード
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import {
  ref, reactive, computed, defineComponent, getCurrentInstance, onMounted,
} from '@vue/composition-api'
import {
  mdiCheckCircleOutline, mdiCloseCircleOutline, mdiPrinterSearch, mdiClose,
  mdiContentCopy, mdiEye, mdiEyeOff,
} from '@mdi/js'
import { cloneDeep } from 'lodash'
import camelcaseKeys from 'camelcase-keys'
import ClubApi from '@/api/waiter/Club'
import KitchenPrinterApi from '@/api/waiter/KitchenPrinter'
import ReceiptPrinterApi from '@/api/waiter/ReceiptPrinter'
import useCurrentData, { setCurrentClub } from '@/views/composable/useCurrentData'
import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import PrinterDiagnosticDialog from '@/views/components/printer/PrinterDiagnosticDialog.vue'
import { openPrinterDiagnostic } from '@/views/composable/usePrinterDiagnostic'

export default defineComponent({
  components: {
    PrinterDiagnosticDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { currentClub } = useCurrentData()
    const { isConnected, checkConnection } = useReceiptPrinterConnection()

    const printIpAddressField = ref(null)
    const receiptPreviewBackgroundImageField = ref(null)
    const isSubmitting = ref(false)
    const onPreviewKey = ref(null)
    const updated = ref(false)
    const backgroundImagePreviewUrl = ref(null)

    // updateClub は club 全フィールドを送信するため、currentClub 全体を form に持って
    // 他項目を保持したまま printIpAddress / プレビュー背景のみ更新する（Club.vue と同じ挙動）
    const form = ref({
      ...cloneDeep(currentClub.value),
      receiptPreviewBackgroundImage: null,
    })

    // レシートプリンタ（printers の receipt 行）のSDP設定。polling_id/password はプリンタ単位。
    const sdpForm = reactive({ sdpEnabled: false, sdpDeviceId: 'local_printer' })
    const receiptPrinter = reactive({
      id: null, sdpPollingId: null, sdpLastPolledAt: null, sdpServerUrl: '',
    })
    const isIssuing = ref(false)
    const isReissueConfirmOpen = ref(false)
    const isSdpPasswordVisible = ref(false)
    const issuedPassword = ref(null) // 発行直後のみ保持（再表示しない）
    let originalSdpEnabled = false

    // SDP有効なキッチンプリンタ一覧（devid 衝突チェック・認証情報共用の選択肢）。
    const sdpKitchenPrinters = ref([])
    // 物理1台でレシート/キッチン兼用するための「認証情報の共用」設定
    const shareWithKitchen = ref(false)
    const shareSourceId = ref(null)
    const isSharing = ref(false)

    onMounted(async () => {
      try {
        const { data } = await ReceiptPrinterApi.getReceiptPrinter()
        sdpForm.sdpEnabled = !!data.sdpEnabled
        sdpForm.sdpDeviceId = data.sdpDeviceId || 'local_printer'
        receiptPrinter.id = data.id
        receiptPrinter.sdpPollingId = data.sdpPollingId
        receiptPrinter.sdpLastPolledAt = data.sdpLastPolledAt
        receiptPrinter.sdpServerUrl = data.sdpServerUrl
        originalSdpEnabled = sdpForm.sdpEnabled
      } catch {
        // 取得失敗時はSDPセクションを初期値のまま表示（保存は妨げない）
      }

      try {
        const res = await KitchenPrinterApi.getKitchenPrinterSettings()
        sdpKitchenPrinters.value = (res?.data?.kitchenPrinters?.data || [])
          .filter(p => p.attributes?.sdpEnabled)
      } catch {
        sdpKitchenPrinters.value = []
      }

      // 既にレシートとキッチンが同じ polling_id を共有していれば、共用状態として復元する
      const shared = sdpKitchenPrinters.value
        .find(p => p.attributes?.sdpPollingId && p.attributes.sdpPollingId === receiptPrinter.sdpPollingId)
      if (shared) {
        shareWithKitchen.value = true
        shareSourceId.value = Number(shared.id)
      }
    })

    // 共用の選択肢（SDP有効キッチンプリンタ）
    const kitchenPrinterOptions = computed(() => sdpKitchenPrinters.value.map(p => ({
      value: Number(p.id),
      text: p.attributes?.name,
    })))

    // 指定キッチンプリンタの polling_id/password をレシートへコピーして共用する
    const applyShareCredentials = async () => {
      if (!shareSourceId.value) return
      isSharing.value = true
      try {
        const { data } = await ReceiptPrinterApi.shareCredentials(shareSourceId.value)
        receiptPrinter.sdpPollingId = data.sdpPollingId
        issuedPassword.value = data.sdpPassword
        isSdpPasswordVisible.value = true
        vm.$toast.success('キッチンプリンタと認証情報を共用しました')
      } catch {
        vm.$toast.error('認証情報の共用に失敗しました')
      } finally {
        isSharing.value = false
      }
    }

    // 会計の devid が SDP有効キッチンプリンタの devid と重複している場合に警告する。
    // ただし共用ON時は devid 一致が正しい状態なので警告しない。
    const conflictingKitchenPrinterNames = computed(() => {
      if (!sdpForm.sdpEnabled || shareWithKitchen.value) return []
      const devid = (sdpForm.sdpDeviceId || '').trim()
      if (!devid) return []

      return sdpKitchenPrinters.value
        .filter(p => p.attributes?.sdpDeviceId === devid)
        .map(p => p.attributes?.name)
        .filter(Boolean)
    })

    const copyToClipboard = async (text, label) => {
      try {
        await navigator.clipboard.writeText(text)
        vm.$toast.success(`${label}をコピーしました`)
      } catch {
        vm.$toast.error('コピーに失敗しました')
      }
    }

    const issueSdpCredentials = async () => {
      isIssuing.value = true
      try {
        const { data } = await ReceiptPrinterApi.issueSdpCredentials()
        receiptPrinter.sdpPollingId = data.sdpPollingId
        issuedPassword.value = data.sdpPassword
        isSdpPasswordVisible.value = true
        vm.$toast.success('SDP認証情報を発行しました')
      } catch {
        vm.$toast.error('SDP認証情報の発行に失敗しました')
      } finally {
        isIssuing.value = false
      }
    }

    // 既に発行済みなら確認ダイアログを挟む（誤タップで既存値が無効化される事故が多発したため）
    const onClickIssueSdpCredentials = () => {
      if (receiptPrinter.sdpPollingId) {
        isReissueConfirmOpen.value = true
      } else {
        issueSdpCredentials()
      }
    }

    const confirmReissueSdpCredentials = async () => {
      await issueSdpCredentials()
      isReissueConfirmOpen.value = false
    }

    const checkConnectionStatus = reactive({
      on: false,
      inProgress: false,
    })

    const resetCheckConnectionStatus = () => {
      checkConnectionStatus.on = false
      checkConnectionStatus.inProgress = false
      isConnected.value = false
    }

    const changePrintIpAddress = ipAddress => {
      resetCheckConnectionStatus()
      form.value.printIpAddress = ipAddress
    }

    const testConnection = async () => {
      isConnected.value = false
      checkConnectionStatus.on = true
      checkConnectionStatus.inProgress = true
      // 設定画面の「接続テスト」は端末ごとの無効設定にかかわらず必ず実行する
      await checkConnection(form.value.printIpAddress, { force: true })
      checkConnectionStatus.inProgress = false
    }

    const previewBackgroundImage = key => {
      let url
      if (form.value[key]) {
        url = URL.createObjectURL(form.value[key])
      } else {
        url = currentClub.value[key]
      }

      if (url) {
        backgroundImagePreviewUrl.value = url
        onPreviewKey.value = key
      }
    }

    const lessThanOrEqualTo2Gb = file => {
      return !file || file.size <= (2 * 1024 * 1024 * 1024) || '2GB以下のサイズにしてください'
    }

    const hasError = computed(() => {
      return printIpAddressField?.value?.hasError
        || receiptPreviewBackgroundImageField?.value?.hasError
    })

    const printerCertUrl = computed(() => {
      const ip = form.value?.printIpAddress
      if (!ip) return null
      const cleanIp = ip.split(':')[0]

      return `https://${cleanIp}:8043/cgi-bin/epos/service.cgi`
    })
    const printerWebConfigUrl = computed(() => {
      const ip = form.value?.printIpAddress
      if (!ip) return null
      const cleanIp = ip.split(':')[0]

      return `https://${cleanIp}/`
    })

    const update = async () => {
      isSubmitting.value = true
      // IPアドレスが変わると印刷用の共有接続(Layout provide)が古いIPのままになるためリロードを促す
      const ipChanged = (currentClub.value?.printIpAddress || '') !== (form.value.printIpAddress || '')
      // SDPのON/OFFを切り替えると、Layoutが起動時に張った直結接続(printer)が残ったままになる。
      // 二重印刷はしない(印刷トリガーはreceiptSdpEnabledをリアクティブに見て分岐する)が、
      // 旧経路の接続がゾンビとして残り同時接続枠を占有するため、リロードで掃除を促す。
      const sdpChanged = originalSdpEnabled !== sdpForm.sdpEnabled

      try {
        // クラブ項目（IP・プレビュー背景）と、レシートプリンタのSDP設定を両方保存する
        const res = await ClubApi.updateClub(form.value)
        await ReceiptPrinterApi.updateReceiptPrinter({
          sdpEnabled: sdpForm.sdpEnabled,
          sdpDeviceId: sdpForm.sdpDeviceId,
        })

        if (res?.data?.club) {
          // updateClub のレスポンス club.receiptSdpEnabled は、後続の updateReceiptPrinter による
          // sdp_enabled 更新「前」のスナップショット（printers.receipt の sdp_enabled から導出されるため）。
          // 古い値で setCurrentClub すると印刷経路の分岐（useSdpSlipPrint）がズレて 422 になるので、
          // 実際に保存した SDP 値で上書きしてから保存する。
          const club = camelcaseKeys(res.data.club)
          club.receiptSdpEnabled = sdpForm.sdpEnabled
          setCurrentClub(club)
          originalSdpEnabled = sdpForm.sdpEnabled
          vm.$toast.success('更新しました')
          if (ipChanged || sdpChanged) updated.value = true
        } else {
          vm.$toast.error('更新できませんでした')
        }
      } catch {
        vm.$toast.error('更新できませんでした')
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      form,
      sdpForm,
      receiptPrinter,
      isIssuing,
      isReissueConfirmOpen,
      onClickIssueSdpCredentials,
      confirmReissueSdpCredentials,
      isSdpPasswordVisible,
      issuedPassword,
      issueSdpCredentials,
      copyToClipboard,
      shareWithKitchen,
      shareSourceId,
      isSharing,
      kitchenPrinterOptions,
      applyShareCredentials,
      printIpAddressField,
      receiptPreviewBackgroundImageField,
      isSubmitting,
      updated,
      isConnected,
      conflictingKitchenPrinterNames,
      checkConnectionStatus,
      onPreviewKey,
      backgroundImagePreviewUrl,
      changePrintIpAddress,
      testConnection,
      previewBackgroundImage,
      lessThanOrEqualTo2Gb,
      hasError,
      printerCertUrl,
      printerWebConfigUrl,
      update,
      openPrinterDiagnostic,
      icons: {
        mdiCheckCircleOutline,
        mdiCloseCircleOutline,
        mdiPrinterSearch,
        mdiClose,
        mdiContentCopy,
        mdiEye,
        mdiEyeOff,
      },
    }
  },
})
</script>

<style scoped>
.preview-bg {
  width: 400px;
  height: 70vh;
  background-color: #fff;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
}
</style>
